import clsx from 'clsx';
import { Layers, RotateCcw } from 'lucide-react';
import {
  PATTERN_PRESETS,
  PRESET_GROUPS,
  STRAIGHT_PRESET_ID,
  type PatternPreset,
} from '../store/presets';

/**
 * Preset shelf.
 *
 * Straight sits outside the list as its own button, because "get me back to the
 * plain chop" is the one thing you want without hunting through a menu.
 */
export function StraightButton({
  onApply,
  color,
}: {
  onApply: (preset: PatternPreset) => void;
  color: string;
}) {
  const straight = PATTERN_PRESETS.find((p) => p.id === STRAIGHT_PRESET_ID)!;
  return (
    <button
      className="btn"
      title="Rebuild this pattern as the plain 1:1 chop of the loop. Always available, never edited."
      style={{ borderColor: `${color}66`, color }}
      onClick={() => onApply(straight)}
    >
      <RotateCcw size={12} />
      Straight
    </button>
  );
}

/** Tiles for one group. Each group gets its own toolbar button. */
export function PresetMenu({
  group,
  onApply,
}: {
  group: string;
  onApply: (preset: PatternPreset) => void;
}) {
  const items = PATTERN_PRESETS.filter((p) => p.group === group && p.id !== STRAIGHT_PRESET_ID);
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-1">
      {items.map((preset) => (
        <button
          key={preset.id}
          title={preset.description}
          onClick={() => onApply(preset)}
          className="rounded-md border border-ink-700 bg-ink-900 px-2 py-1.5 text-left transition-colors hover:border-volt-500 hover:bg-volt-500/10"
        >
          <div className="truncate text-[11px] font-medium">{preset.name}</div>
          <div className="truncate text-[9px] leading-tight text-ink-500">{preset.description}</div>
        </button>
      ))}
    </div>
  );
}

/** Toolbar toggle — presets stack instead of replacing. */
export function MixToggle({ mix, onChange }: { mix: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      className={clsx('btn', mix && 'btn-primary')}
      onClick={() => onChange(!mix)}
      title={
        mix
          ? 'Mix on — presets stack onto the pattern you already have'
          : 'Mix off — each preset rebuilds from a straight chop'
      }
    >
      <Layers size={12} />
      Mix {mix ? 'on' : 'off'}
    </button>
  );
}

/** Groups that actually have presets in them, in declaration order. */
export const PRESET_MENUS = PRESET_GROUPS.filter((g) =>
  PATTERN_PRESETS.some((p) => p.group === g && p.id !== STRAIGHT_PRESET_ID),
);

export function presetCount(group: string): number {
  return PATTERN_PRESETS.filter((p) => p.group === group && p.id !== STRAIGHT_PRESET_ID).length;
}
